import { useState } from 'react'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useSubmitStocktake } from '@/hooks/use-stocktake-query'
import { m } from '@/lib/paraglide/messages'

interface Props {
    open: boolean
    onOpenChange: (v: boolean) => void
    sessionId: string
    counted: number
    total: number
    variance: number
}

export function StocktakeSubmitDialog({
    open,
    onOpenChange,
    sessionId,
    counted,
    total,
    variance,
}: Props) {
    const workflow = useWorkflowTracking('stocktake_submit', {
        operation: 'submit',
        active: open,
        attemptKey: sessionId,
    })
    const submit = useSubmitStocktake()
    const [confirmed, setConfirmed] = useState(false)

    const handleSubmit = async () => {
        setConfirmed(true)
        workflow.interact('review')
        try {
            await workflow.run(() => submit.mutateAsync(sessionId))
            onOpenChange(false)
        } catch {
            // The mutation hook owns the API error toast.
        } finally {
            setConfirmed(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {m.stocktake_submit_dialog_title()}
                    </DialogTitle>
                    <DialogDescription>
                        {m.stocktake_submit_dialog_desc()}
                    </DialogDescription>
                </DialogHeader>
                <ul className="space-y-1 text-sm list-disc pl-6">
                    <li>
                        {m.stocktake_submit_dialog_lines({
                            counted: String(counted),
                            total: String(total),
                        })}
                    </li>
                    <li>
                        {m.stocktake_submit_dialog_variance({
                            count: String(variance),
                        })}
                    </li>
                </ul>
                {variance > 0 && (
                    <p className="text-sm text-muted-foreground">
                        {m.stocktake_submit_dialog_after()}
                    </p>
                )}
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={submit.isPending}
                    >
                        {m.common_cancel()}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={submit.isPending || confirmed}
                    >
                        {m.stocktake_action_confirm()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
